import { useEffect, useMemo, useState } from 'react';
import { useRouter } from 'next/router';
import { cx } from '@/utils';
import SvgArrow from '@common/Icons/Arrow';
import SvgMapMarker from '@common/Icons/MapMarker';
import SvgWeather from '@common/Icons/Weather';
import { t } from '@lingui/macro';
import { TitleWrapper } from '@views/Home/components';
import { useHomeContext } from '@views/Home/HomeProvider/HomeContext';
import { format, isSameDay, isTomorrow, isYesterday } from 'date-fns';
import { capitalize } from 'lodash';
import Calendar from 'react-calendar';

import EventsCard from '@/components/blocks/PageEventsBlock/public/EventsCard';

import 'react-calendar/dist/Calendar.css';

import Image from 'next/image';
import { useJsApiLoader } from '@react-google-maps/api';
import { env } from 'env.mjs';
import moment from 'moment';

import { formatDate } from '@/utils/dateHelpers';
import Modal from '@/components/common/Modals/Modal';
import {
  EventsItem,
  PageEventsBlock,
} from '@/components/common/Providers/GraphqlProvider/generated';
import { Button } from '@/components/ui/Button';
import { Typography } from '@/components/ui/Typography';
import { splitTitle } from '@/components/views/Home/components/TitleWrapper';

import { EventDetailInMap } from '../../../views/Home/EventItem/EventDetailInMap';
import { useEventsItemsFiltered } from './hooks/useGetEventsFiltered';
import styles from './styles.module.css';

const zoom = 18;

const Events = ({
  noTitle,
  currentPageEventsBlock,
}: {
  noTitle?: boolean;
  currentPageEventsBlock?: PageEventsBlock;
}) => {
  const {
    home: { eventsBlock: eventsBlockContext },
  } = useHomeContext();

  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: env.NEXT_PUBLIC_GOOGLE_MAP_KEY,
  });

  const eventsBlock = currentPageEventsBlock || eventsBlockContext;

  // Use the filtered events hook
  const { data: filteredEvents, isLoading: isLoadingEvents } = useEventsItemsFiltered({
    eventsBlock,
    enabled: !eventsBlock?.isDisabled,
  });

  const { locale, push } = useRouter();
  const [date, setDate] = useState<any>(new Date());

  //map variables
  const [mapOpened, setMapOpened] = useState(false);
  const [center, setCenter] = useState<google.maps.LatLngLiteral>();
  const [clicks, setClicks] = useState<google.maps.LatLng>();
  const [selectedEvent, setSelectedEvent] = useState<EventsItem | undefined>(undefined);
  const [locationFound, setLocationFound] = useState<number | undefined>(undefined);

  function tileClassName({ date, view }: any) {
    if (view === 'month') {
      if (
        filteredEvents?.some(
          (item) => formatDate(date, 'YYYY-MM-DD') === formatDate(item.startDate, 'YYYY-MM-DD')
        )
      ) {
        return 'event-date';
      }
    }
  }

  const dates = useMemo(() => {
    return (
      filteredEvents?.filter(
        (item) => formatDate(date, 'YYYY-MM-DD') === formatDate(item.startDate, 'YYYY-MM-DD')
      ) || []
    );
  }, [date, filteredEvents]);

  const getCurrentTime = (date: Date) => {
    const _date = new Date(date);
    const currentHours = new Date().getHours();
    const currentMinutes = new Date().getMinutes();
    _date.setHours(currentHours);
    _date.setMinutes(currentMinutes);
    return _date;
  };

  const goToEvents = () => {
    push('/events');
  };

  const goToEvent = (id: string) => {
    push(`/events/${id}`);
  };

  function openMap(item: EventsItem) {
    setSelectedEvent(item);
    if (item.latitude && item.longitude && isLoaded) {
      setCenter({
        lat: Number(item.latitude),
        lng: Number(item.longitude),
      });

      try {
        const location = new google.maps.LatLng({
          lat: Number(item.latitude),
          lng: Number(item.longitude),
        });

        setClicks(location);
        setLocationFound(Date.now());
      } catch (e) {
        try {
          const location = new google.maps.LatLng({
            lat: Number(item.latitude),
            lng: Number(item.longitude),
          });
          setClicks(location);
          setLocationFound(Date.now());
        } catch (err) {}
      }
    }
  }

  useEffect(() => {
    if (locationFound) {
      setMapOpened(true);
    }
  }, [locationFound, selectedEvent, clicks, center]);

  function closeMap() {
    setMapOpened(false);
  }

  if (eventsBlock?.isDisabled) return null;

  const [title1, title2] = splitTitle(eventsBlock?.eventTitle, {
    show: eventsBlock?.showTitle,
    defaultTitle1: 'No te pierdas',
    defaultTitle2: 'los próximos eventos',
  });

  return (
    <section className={'mt-10'}>
      <div className={'inner-extend'}>
        <div className={'mb-4 md:mb-8'}>
          {noTitle ? null : (
            <TitleWrapper
              title1={title1}
              title2={title2}
              description={!eventsBlock?.showDescription ? '' : eventsBlock?.eventDescription}
              action={
                eventsBlock?.seeMoreLink
                  ? {
                      text: eventsBlock.seeMoreText as string,
                      onClick: () => {
                        if (eventsBlock?.seeMoreLink) {
                          window.open(eventsBlock.seeMoreLink);
                        }
                      },
                    }
                  : undefined
              }
            />
          )}
        </div>
        <div className="hidden flex-wrap items-start gap-[16px] self-stretch md:flex md:flex-row md:space-x-[24px]">
          <div className="flex flex-1 flex-col items-start gap-[16px] self-stretch md:flex-[1_0_0] md:flex-row">
            <div className="flex min-w-40 flex-[1_0_0] flex-col items-center justify-center gap-[24px] self-stretch rounded-[10px] bg-primary p-[16px] text-center xl:min-w-60">
              <div className="headline-h3 text-white">
                {isSameDay(new Date(), new Date(date)) && 'Hoy'}
                {isTomorrow(new Date(date)) && 'Mañana'}
                {isYesterday(new Date(date)) && 'Ayer'}
              </div>
              <div>
                <div className="font-montserrat text-[24px] font-semibold not-italic leading-[36px] text-white">
                  {format(getCurrentTime(date), 'hh:mm')}
                </div>
                <div className="headline-h4 text-white">{capitalize(format(date, 'cccc'))}</div>
              </div>
              <div>
                <div className="text-description text-white">
                  {capitalize(format(date, 'LLLL'))}
                </div>
                <div className="font-montserrat text-[80px] font-semibold not-italic leading-[96px] text-white">
                  {new Date(date).getDate()}
                </div>
              </div>
              <div className="flex justify-center gap-[14px] self-stretch align-baseline">
                <SvgWeather className="text-white" />
                <div className="font-workSans text-[24px] font-medium not-italic leading-[24px] text-white">
                  34°C
                </div>
              </div>
            </div>
            <div className="flex h-[442px] flex-col items-center justify-start gap-[21px] self-stretch rounded-[10px] border-2 border-solid border-blueLight bg-white p-[24px_16px_16px_16px]">
              <div>
                <Calendar
                  locale={locale}
                  onChange={(value: any) => setDate(new Date(value))}
                  formatMonthYear={(locale, date) =>
                    date.toLocaleString(locale, {
                      month: 'long',
                    })
                  }
                  tileClassName={tileClassName}
                  value={date}
                  minDetail={'month'}
                  prev2Label={null}
                  next2Label={null}
                  prevLabel={
                    <div className={'flex w-full rotate-180 justify-center'}>
                      <SvgArrow width={15} height={15} className="text-primary" />
                    </div>
                  }
                  nextLabel={
                    <div className={'flex w-full justify-center'}>
                      <SvgArrow width={15} height={15} className="text-primary" />
                    </div>
                  }
                />
              </div>
            </div>
          </div>
          <div className={styles.cards}>
            {!!dates.length ? (
              dates.slice(0, 4).map((_date, key) => (
                <div className={styles.card} key={key}>
                  <div className={cx(styles.headerCard, 'text-tundora')}>
                    <div>
                      <Typography variant="body2" as="span" className={'font-bold'}>
                        {'Desde:'}
                      </Typography>{' '}
                      <Typography variant="body2" as="span">
                        {moment.utc(_date.startDate).format('YYYY-MM-DD hh:mm:ss a')}
                      </Typography>
                    </div>
                    <div>
                      <Typography variant="body2" as="span" className={'font-bold'}>
                        {'Hasta:'}
                      </Typography>{' '}
                      <Typography variant="body2" as="span">
                        {moment.utc(_date.endDate).format('YYYY-MM-DD hh:mm:ss a')}
                      </Typography>
                    </div>
                  </div>
                  <div className={styles.contentCard}>
                    <Typography
                      variant="body1"
                      className={'cursor-pointer text-ellipsis font-semibold text-black'}
                      onClick={() => goToEvent(_date.id)}
                    >
                      {_date.eventItemTitle}
                    </Typography>
                    <div
                      onClick={() => openMap(_date as EventsItem)}
                      className={'group flex cursor-pointer items-baseline gap-[8px] self-stretch'}
                    >
                      <SvgMapMarker className="text-primary" />
                      <Typography
                        variant="body2"
                        as="span"
                        className={cx(styles.location, 'group-hover:underline')}
                      >
                        {_date.address}{' '}
                        {_date.descriptionPlace
                          ? `(${_date.descriptionPlace})`
                          : _date.descriptionPlace}
                      </Typography>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="mx-auto flex h-full flex-col items-center justify-between gap-6 text-center">
                <Typography variant="h5" className={'text-dimGray'}>
                  {'No hay eventos cercanos'}
                </Typography>
                <Image
                  src={'/noEvents.png'}
                  alt={'No hay eventos cercanos'}
                  width={247}
                  height={232}
                />
                <Typography variant="body1" className={'text-dimGray'}>
                  {
                    'Se previsualizará aquí cuando haya algún evento, si quieres ver más eventos haz clic aquí.'
                  }
                </Typography>
                <Button className="flex gap-4 pl-3" onClick={() => goToEvents()}>
                  {'Ver eventos'}
                  <span className="flex h-6 w-6 items-center justify-center rounded-full bg-white text-primary">
                    <SvgArrow width={16} height={16} />
                  </span>
                </Button>
              </div>
            )}
          </div>
        </div>

        <EventsCard events={filteredEvents as EventsItem[]} />

        <Modal
          open={mapOpened}
          handler={setMapOpened}
          size="xl"
          className="overflow-hidden rounded-lg"
        >
          <EventDetailInMap
            center={center}
            clicks={clicks}
            eventItem={selectedEvent as EventsItem}
            zoom={zoom}
            closeMap={closeMap}
          />
        </Modal>
      </div>
    </section>
  );
};

export default Events;

export function EventsSkeleton() {
  return (
    <section className="mt-10">
      <div className="inner-extend">
        <div className="mb-4 md:mb-8">
          <div className="h-4 w-48 animate-pulse rounded bg-gray-200" />
        </div>

        <div className="hidden flex-wrap items-start gap-[16px] self-stretch md:flex md:flex-row md:space-x-[24px]">
          <div className="flex flex-1 flex-col items-start gap-[16px] self-stretch md:flex-[1_0_0] md:flex-row">
            {/* Static date box */}
            <div className="flex min-w-40 flex-[1_0_0] flex-col items-center justify-center gap-[24px] self-stretch rounded-[10px] bg-primary p-[16px] text-center xl:min-w-60">
              <div className="headline-h3 text-white">Hoy</div>
              <div>
                <div className="font-montserrat text-[24px] font-semibold not-italic leading-[36px] text-white">
                  08:00
                </div>
                <div className="headline-h4 text-white">Lunes</div>
              </div>
              <div>
                <div className="text-description text-white">Enero</div>
                <div className="font-montserrat text-[80px] font-semibold not-italic leading-[96px] text-white">
                  12
                </div>
              </div>
              <div className="flex justify-center gap-[14px] self-stretch align-baseline">
                <SvgWeather className="text-white" />
                <div className="font-workSans text-[24px] font-medium not-italic leading-[24px] text-white">
                  34°C
                </div>
              </div>
            </div>

            {/* Calendar skeleton */}
            <div className="flex h-[442px] flex-col items-center justify-start gap-[21px] self-stretch rounded-[10px] border-2 border-solid border-blueLight bg-white p-[24px_16px_16px_16px]">
              <Calendar
                value={new Date()}
                onChange={() => {}}
                tileClassName={() => 'bg-gray-200 animate-pulse !text-transparent'}
                tileDisabled={() => true}
                minDetail={'month'}
                prev2Label={null}
                next2Label={null}
                prevLabel={
                  <div className={'flex w-full rotate-180 justify-center'}>
                    <SvgArrow width={15} height={15} className="text-primary" />
                  </div>
                }
                nextLabel={
                  <div className={'flex w-full justify-center'}>
                    <SvgArrow width={15} height={15} className="text-primary" />
                  </div>
                }
                showNeighboringMonth={false}
              />
            </div>
          </div>

          {/* Events list skeletons */}
          <div className={styles.cards}>
            {Array.from({ length: 4 }).map((_, idx) => (
              <div key={idx} className={styles.card}>
                <div className={cx(styles.headerCard, 'text-tundora')}>
                  <div className="space-y-2">
                    <div className="h-4 w-24 animate-pulse rounded bg-gray-200" />
                    <div className="h-4 w-28 animate-pulse rounded bg-gray-200" />
                  </div>
                  <div className="space-y-2">
                    <div className="h-4 w-24 animate-pulse rounded bg-gray-200" />
                    <div className="h-4 w-20 animate-pulse rounded bg-gray-200" />
                  </div>
                </div>
                <div className={styles.contentCard}>
                  <div className="h-5 w-3/4 animate-pulse rounded bg-gray-200" />
                  <div className={'group flex items-baseline gap-[8px] pt-3'}>
                    <div className="h-4 w-4 animate-pulse rounded-full bg-gray-200" />
                    <div className="h-4 w-1/2 animate-pulse rounded bg-gray-200" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Mobile / fallback simple skeleton */}
        <div className="animate-pulse md:hidden">
          <div className="mb-4 h-40 w-full rounded bg-gray-200" />
          <div className="space-y-4">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="h-24 w-full rounded bg-gray-200" />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
